import React, { useEffect, useRef } from 'react'
import profileImg from '../assets/portfolio.jpeg'

const AboutMe = () => {
  const parallaxRef = useRef(null)

  useEffect(() => {
    let raf = null

    const update = () => {
      const el = parallaxRef.current
      if (!el) return

      const rect = el.getBoundingClientRect()
      // Calculate a subtle offset based on distance from viewport center
      const centerY = window.innerHeight / 2
      const dist = rect.top + rect.height / 2 - centerY
      const offset = dist * 0.04 // gentle parallax multiplier

      // Apply transform with GPU-accelerated translateZ trick
      el.style.transform = `translateY(${offset}px)`
      raf = requestAnimationFrame(update)
    }

    raf = requestAnimationFrame(update)
    window.addEventListener('resize', () => {
      if (!raf) raf = requestAnimationFrame(update)
    }, { passive: true })

    return () => {
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <section
      id="about"
        className="scroll-mt-16 bg-slate-50 py-6 md:py-8 dark:bg-slate-950"
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="reveal">
          <h2 className="text-center text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
            About Me
          </h2>
          <div className="mx-auto mt-3 h-1 w-12 rounded bg-primary" />
        </div>

        <div className="mt-8 grid items-start gap-10 lg:gap-12 lg:grid-cols-[auto,1fr] lg:items-center">
          {/* Profile / left column */}
          <div className="reveal reveal-delay-1 flex flex-col items-center">
            <div className="relative">
              <div className="absolute -inset-6 -z-10 rounded-full bg-primary/10 blur-2xl" aria-hidden="true" />
              <div
                ref={parallaxRef}
                className="img-zoom relative h-56 w-56 overflow-hidden rounded-full ring-4 ring-white sm:h-64 sm:w-64 md:h-64 md:w-64 lg:h-72 lg:w-72 dark:ring-slate-800"
                style={{ willChange: 'transform' }}
              >
                  <img
                    src={profileImg}
                    alt="Omkar Kardel"
                    className="h-full w-full object-cover object-center"
                    loading="lazy"
                  />
              </div>
            </div>

            <p className="mt-5 text-center text-base font-medium text-slate-600 sm:text-lg dark:text-slate-300">
              <span className="text-primary">Full Stack MERN Developer </span> · AWS & DevOps Learner
            </p>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200">
                MERN
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200">
                AWS / Docker
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200">
                CI/CD
              </span>
            </div>
          </div>

          {/* Content / right column */}
          <div>
            <p className="text-sm font-medium uppercase tracking-wider text-primary">Hello, I'm</p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl dark:text-white">
              Omkar Kardel
            </h1>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Focused on building real, usable products</h2>

              <div className="mt-3 space-y-4">
                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  I'm <span className="font-semibold text-primary">Omkar Kardel</span>, an Information Technology student who turns ideas into working web applications.
                  I care about interfaces that are easy to use, APIs that are organized, and projects that can actually be deployed and improved.
                </p>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">What you can expect</p>
                    <ul className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                      <li className="flex gap-2"><span className="mt-1 text-primary">•</span>Responsive, polished React screens</li>
                      <li className="flex gap-2"><span className="mt-1 text-primary">•</span>Clear API integration and data flow</li>
                      <li className="flex gap-2"><span className="mt-1 text-primary">•</span>Ownership from idea to deployment</li>
                    </ul>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Currently sharpening</p>
                    <ul className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                      <li className="flex gap-2"><span className="mt-1 text-primary">•</span>Dockerized app workflows</li>
                      <li className="flex gap-2"><span className="mt-1 text-primary">•</span>AWS deployment fundamentals</li>
                      <li className="flex gap-2"><span className="mt-1 text-primary">•</span>Better testing and maintainability</li>
                    </ul>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <a
                    href="#projects"
                    className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-dark"
                  >
                    View Projects
                  </a>
                  <a
                    href="#contact"
                    className="rounded-md border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-primary dark:border-slate-600 dark:text-slate-200"
                  >
                    Let&apos;s Talk
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AboutMe
